import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeDollarSign, Check, Link2, Megaphone, ShieldCheck, Video } from "lucide-react";
import { Seo } from "@/components/hv/Seo";
import { FadeIn, MaskedLines, Reveal } from "@/components/hv/Motion";
import { LogoMark } from "@/components/hv/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const FITS = [
  { t: "Caregiving & sandwich-generation creators", d: "Daughters and sons managing a parent's paperwork from a distance. Your audience already lives the problem." },
  { t: "Personal-finance & money-organisation channels", d: "\"Get your documents in order\" content converts here better than anywhere." },
  { t: "Estate attorneys & financial planners with an audience", d: "Keep the plan you built current. Portal access and a co-branded checklist for your clients." },
  { t: "Home-organisation & minimalism accounts", d: "The empty drawer. The sorted shoebox. The one-paper-a-week ritual." },
];

const FAQ = [
  ["How is a referral tracked?", "You get a personal link (follow.hearthvault.family/?ref=yourcode). Anyone who arrives through it is credited to you for 30 days — even if they sign up later."],
  ["When do I get paid?", "Monthly, for everything your referred families paid in the previous month, for the first 12 months of each subscription. Bonus paid the month it's reached."],
  ["Can I see my numbers?", "Yes — we send a monthly statement with clicks, signups, active families and the amount due. Ask any time and we'll share the live figures."],
  ["Do I need to say it's sponsored?", "Yes. Use #ad or #affiliate and the platform's paid-partnership label. We'll never ask you to hide it — honesty is the whole brand."],
  ["What about dedicated videos?", "If you'd like to make a dedicated piece, we pay a flat fee on top of the revenue share and license the video for our own pages. Mention it in your application."],
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{label}</span>
    {children}
  </label>
);
const inputCls = "mt-2 w-full h-12 rounded-full border border-[#E8DDD3] bg-[#FBF8F4] px-5 text-sm text-[#1F1A17] outline-none focus:border-[#1F1A17] transition-colors placeholder:text-[#B5ABA3]";

const ApplyForm = () => {
  const [f, setF] = useState({ name: "", email: "", platform: "instagram", handle: "", audience: "", why: "", requestedCode: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setState("sending");
    const r = await fetch(`${API}/api/partners/apply`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
    setState(r.ok ? "done" : "error");
  };
  if (state === "done") {
    return (
      <div className="rounded-[2rem] bg-[#181412] text-[#FBF8F4] p-8 sm:p-12" data-testid="partner-apply-success">
        <span className="h-12 w-12 rounded-full grid place-items-center bg-[#B4532A]"><Check className="h-5 w-5" /></span>
        <h3 className="mt-6 font-serif text-3xl tracking-tight">Thank you, {f.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-[#C8BFB8] leading-relaxed">We read every application personally and reply within a few days with your link and the terms in writing. In the meantime, the <Link to="/checklist" className="underline decoration-[#D4A373] underline-offset-4">kitchen-table checklist</Link> is a good first thing to share.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="rounded-[2rem] border border-[#E8DDD3] bg-white p-8 sm:p-10 grid sm:grid-cols-2 gap-5" data-testid="partner-apply-form">
      <Field label="Your name"><input required value={f.name} onChange={set("name")} className={inputCls} placeholder="Sarah Lane" data-testid="partner-name" /></Field>
      <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="partner-email" /></Field>
      <Field label="Main platform">
        <select value={f.platform} onChange={set("platform")} className={inputCls} data-testid="partner-platform">
          {["instagram", "youtube", "facebook", "linkedin", "tiktok", "newsletter", "podcast", "other"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
        </select>
      </Field>
      <Field label="Handle or channel link"><input value={f.handle} onChange={set("handle")} className={inputCls} placeholder="@moneymom" data-testid="partner-handle" /></Field>
      <Field label="Audience size (rough)"><input value={f.audience} onChange={set("audience")} className={inputCls} placeholder="25k followers · 4k newsletter" data-testid="partner-audience" /></Field>
      <Field label="Preferred code (optional)"><input value={f.requestedCode} onChange={set("requestedCode")} className={inputCls} placeholder="moneymom" maxLength={24} data-testid="partner-code" /></Field>
      <div className="sm:col-span-2">
        <Field label="Why HearthVault fits your audience">
          <textarea value={f.why} onChange={set("why")} rows={4} maxLength={1000} className="mt-2 w-full rounded-3xl border border-[#E8DDD3] bg-[#FBF8F4] px-5 py-4 text-sm text-[#1F1A17] outline-none focus:border-[#1F1A17] transition-colors placeholder:text-[#B5ABA3]" placeholder="Two or three sentences is plenty." data-testid="partner-why" />
        </Field>
      </div>
      <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
        <button type="submit" disabled={state === "sending"} data-testid="partner-apply-submit" className="group inline-flex items-center gap-2 rounded-full bg-[#B4532A] hover:bg-[#9C421E] disabled:opacity-60 h-12 px-6 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
          {state === "sending" ? "Sending…" : "Apply to partner"} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        {state === "error" && <p className="text-sm text-[#B4532A]" data-testid="partner-apply-error">Something went wrong — please try again.</p>}
        <span className="text-xs text-[#685F58]">We reply within a few days. No spam, no list.</span>
      </div>
    </form>
  );
};

export default function PartnersPage() {
  const [t, setT] = useState({ commissionPct: 25, commissionMonths: 12, bonusAmount: 100, bonusThreshold: 25 });
  useEffect(() => { fetch(`${API}/api/partners/terms`).then((r) => r.json()).then(setT).catch(() => {}); }, []);

  const cards = [
    { Icon: BadgeDollarSign, t: `${t.commissionPct}% of every payment`, d: `For the first ${t.commissionMonths} months of each family you refer. Recurring, paid monthly, no cap.` },
    { Icon: Megaphone, t: `$${t.bonusAmount} at ${t.bonusThreshold} families`, d: "A bonus the month you reach your first milestone of active, document-uploading families." },
    { Icon: Video, t: "Flat fee for dedicated pieces", d: "A dedicated video or post earns a fee on top of the share — and we license it for our own pages." },
    { Icon: Link2, t: "Your own link and code", d: "30-day attribution, so a viewer who signs up next week still counts as yours." },
  ];

  return (
    <div data-testid="partners-page">
      <Seo title="Creators & partners — HearthVault" description={`Share HearthVault with your audience and earn ${t.commissionPct}% of every payment for ${t.commissionMonths} months. For caregiving, personal-finance and home-organisation creators, and advisors with an audience.`} image="/og/hub.jpg" />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-end">
        <div>
          <FadeIn className="flex items-center gap-3 text-[#B4532A]">
            <LogoMark className="h-5 w-5" /><span className="font-mono text-xs uppercase tracking-[0.25em]">Creators &amp; partners</span>
          </FadeIn>
          <MaskedLines as="h1" lines={["Tell your people.", "Earn while they get sorted."]} className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#1F1A17]" testId="partners-title" />
          <FadeIn delay={0.5} className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#685F58]">
            HearthVault is the family document safe. If your audience is sorting a shoebox, caring for a parent, or helping clients keep a plan current, we'd like to pay you properly for introducing them.
          </FadeIn>
          <FadeIn delay={0.6} className="mt-8 flex flex-wrap gap-3">
            <a href="#apply" data-testid="partners-hero-apply" className="group inline-flex items-center gap-2 rounded-full bg-[#B4532A] hover:bg-[#9C421E] h-12 px-6 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">Apply in two minutes <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></a>
            <a href="#terms" className="inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] bg-white hover:border-[#1F1A17] h-12 px-6 text-sm font-medium text-[#1F1A17] transition-colors duration-300">See the terms</a>
          </FadeIn>
        </div>
        <FadeIn delay={0.3} className="rounded-[2rem] bg-[#181412] text-[#FBF8F4] p-8">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#D4A373]">Worked example</div>
          <p className="mt-4 font-serif text-2xl leading-snug tracking-tight">One honest video sends 40 families to HearthVault at $10/month.</p>
          <p className="mt-4 text-sm text-[#C8BFB8] leading-relaxed">{t.commissionPct}% × $10 × 40 families = <strong className="text-[#FBF8F4]">${(t.commissionPct / 100 * 10 * 40).toFixed(0)} a month</strong> for {t.commissionMonths} months, plus the ${t.bonusAmount} bonus when {t.bonusThreshold} of them are active. Roughly <strong className="text-[#FBF8F4]">${Math.round(t.commissionPct / 100 * 10 * 40 * t.commissionMonths + t.bonusAmount).toLocaleString()}</strong> from one piece of content.</p>
          <p className="mt-4 text-xs text-[#8D837B]">Illustrative. Actual plan prices and conversion vary.</p>
        </FadeIn>
      </section>

      <section id="terms" className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 scroll-mt-24" data-testid="partners-terms">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-[#E8DDD3] bg-white p-7">
                <span className="h-11 w-11 rounded-xl grid place-items-center bg-[#FDEEE8] text-[#B4532A]"><c.Icon className="h-5 w-5" /></span>
                <h2 className="mt-5 font-serif text-2xl tracking-tight text-[#1F1A17]">{c.t}</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#685F58]">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16" data-testid="partners-fit">
        <Reveal>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">Who this is for</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#1F1A17]">Not every audience. <em>Yours, if…</em></h2>
          <p className="mt-4 text-sm leading-relaxed text-[#685F58]">We'd rather work with twenty creators whose audiences genuinely need this than two hundred who don't. Small, specific channels do best here.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-4">
          {FITS.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-[#E8DDD3] bg-white p-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#685F58]">0{i + 1}</div>
                <h3 className="mt-3 font-serif text-xl tracking-tight text-[#1F1A17]">{f.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#685F58]">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="partners-faq">
        <Reveal className="rounded-[2rem] border border-[#E8DDD3] bg-white p-8 sm:p-12 grid lg:grid-cols-[1fr_1.6fr] gap-10">
          <div>
            <ShieldCheck className="h-6 w-6 text-[#B4532A]" />
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-[#1F1A17]">Plain answers</h2>
          </div>
          <dl className="divide-y divide-[#E8DDD3]">
            {FAQ.map(([q, a]) => (
              <div key={q} className="py-5 first:pt-0 last:pb-0">
                <dt className="font-serif text-xl tracking-tight text-[#1F1A17]">{q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-[#685F58]">{a}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </section>

      <section id="apply" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32 scroll-mt-24 grid lg:grid-cols-[1fr_1.6fr] gap-10 lg:gap-16 items-start" data-testid="partners-apply">
        <Reveal className="lg:sticky lg:top-28">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">Apply</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#1F1A17]">Two minutes. <em>A real person reads it.</em></h2>
          <p className="mt-4 text-sm leading-relaxed text-[#685F58]">Tell us where your audience lives and why this fits. If it's a match, you'll have your link and the terms in writing within a few days.</p>
        </Reveal>
        <Reveal delay={0.1}><ApplyForm /></Reveal>
      </section>
    </div>
  );
}
